import { useCallback, useSyncExternalStore } from 'react'

/**
 * Per-browser UI preferences (language, theme, density, sidebar, column visibility) persisted in
 * localStorage and shared across components through useSyncExternalStore. Storage failures
 * (private mode, quota) degrade to in-memory values.
 */
const PREFIX = 'sahab.'
const listeners = new Map<string, Set<() => void>>()
const memory = new Map<string, string>()

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(PREFIX + key)
  } catch {
    return memory.get(key) ?? null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(PREFIX + key)
    else window.localStorage.setItem(PREFIX + key, value)
  } catch {
    if (value === null) memory.delete(key)
    else memory.set(key, value)
  }
  for (const listener of listeners.get(key) ?? []) listener()
}

function subscribe(key: string, listener: () => void): () => void {
  let set = listeners.get(key)
  if (!set) {
    set = new Set()
    listeners.set(key, set)
  }
  set.add(listener)
  const onStorage = (e: StorageEvent) => {
    if (e.key === PREFIX + key) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    set.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

export interface Preference<T> {
  get(): T
  /** Whether the user made an explicit choice (as opposed to the fallback). */
  isSet(): boolean
  set(value: T): void
  subscribe(listener: () => void): () => void
}

/** A typed preference. `parse` returns undefined for unknown/corrupt stored values. */
export function definePreference<T>(
  key: string,
  fallback: T,
  parse: (raw: string) => T | undefined,
  serialize: (value: T) => string = String,
): Preference<T> {
  // Cache by raw string so useSyncExternalStore sees a stable snapshot for object values.
  let lastRaw: string | null | undefined
  let lastValue: T = fallback
  return {
    get() {
      const raw = read(key)
      if (raw !== lastRaw) {
        lastRaw = raw
        lastValue = raw === null ? fallback : (parse(raw) ?? fallback)
      }
      return lastValue
    },
    isSet() {
      const raw = read(key)
      return raw !== null && parse(raw) !== undefined
    },
    set(value) {
      write(key, serialize(value))
    },
    subscribe(listener) {
      return subscribe(key, listener)
    },
  }
}

export function enumPreference<const T extends string>(key: string, values: readonly T[], fallback: T) {
  return definePreference<T>(key, fallback, (raw) => values.find((v) => v === raw))
}

export function jsonPreference<T>(key: string, fallback: T, guard: (value: unknown) => value is T) {
  return definePreference<T>(
    key,
    fallback,
    (raw) => {
      try {
        const value: unknown = JSON.parse(raw)
        return guard(value) ? value : undefined
      } catch {
        return undefined
      }
    },
    (value) => JSON.stringify(value),
  )
}

export function usePreference<T>(pref: Preference<T>): [T, (value: T) => void] {
  const value = useSyncExternalStore(pref.subscribe, pref.get, pref.get)
  const set = useCallback((next: T) => pref.set(next), [pref])
  return [value, set]
}
